// File: src/components/checkout/CoinsRedeemBox.js

import React, { useEffect, useMemo, useState } from "react";
import api from "../../api/client";

function clampNumber(value, min = 0, max = Number.MAX_SAFE_INTEGER) {
  const n = parseInt(value, 10);
  if (Number.isNaN(n)) return 0;
  return Math.max(min, Math.min(max, n));
}

function usdFromCoins(coins) {
  return Number((coins / 100).toFixed(2));
}

function coinsFromUsd(usd) {
  return Math.max(0, Math.floor(Number(usd || 0) * 100));
}

export default function CoinsRedeemBox({
  subtotal = 0,
  onCoinsChange,
  disabled = false,
  isAr = false,
}) {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coinsInput, setCoinsInput] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    async function loadWallet() {
      try {
        setLoading(true);
        setError("");

        const res = await api.apiGet("rewards/wallet");

        // Laravel clean response
        const walletData = res?.data || null;

        if (!mounted) return;
        setWallet(walletData);
      } catch (err) {
        if (!mounted) return;
        setError(isAr ? "فشل تحميل المحفظة." : "Failed to load wallet.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadWallet();

    return () => {
      mounted = false;
    };
  }, [isAr]);

  const walletCoins = Number(wallet?.coins || 0);

  const maxCoinsBySubtotal = useMemo(() => {
    const maxDiscountUsd = Math.max(0, Number(subtotal || 0));
    return coinsFromUsd(maxDiscountUsd);
  }, [subtotal]);

  const maxUsableCoins = useMemo(() => {
    return Math.max(0, Math.min(walletCoins, maxCoinsBySubtotal));
  }, [walletCoins, maxCoinsBySubtotal]);

  const appliedCoins = useMemo(() => {
    return clampNumber(coinsInput, 0, maxUsableCoins);
  }, [coinsInput, maxUsableCoins]);

  const discountUsd = useMemo(() => {
    return usdFromCoins(appliedCoins);
  }, [appliedCoins]);

  const finalTotal = useMemo(() => {
    return Number(
      Math.max(0, Number(subtotal || 0) - discountUsd).toFixed(2)
    );
  }, [subtotal, discountUsd]);

  useEffect(() => {
    if (typeof onCoinsChange === "function") {
      onCoinsChange({
        coins_to_use: appliedCoins,
        coins_discount: discountUsd,
        final_total: finalTotal,
        max_usable_coins: maxUsableCoins,
      });
    }
  }, [appliedCoins, discountUsd, finalTotal, maxUsableCoins, onCoinsChange]);

  const handleInputChange = (e) => {
    const raw = e.target.value;
    const next = raw === "" ? 0 : clampNumber(raw, 0, maxUsableCoins);
    setCoinsInput(next);
  };

  return (
    <div className="border rounded-3 p-3 bg-white shadow-sm mt-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">{isAr ? "استخدام النقاط" : "Use Coins"}</h5>

        {loading ? (
          <span className="text-muted small">
            {isAr ? "جارٍ التحميل..." : "Loading..."}
          </span>
        ) : (
          <span className="text-muted small">
            {isAr ? "الرصيد" : "Balance"}: <strong>{walletCoins}</strong>
          </span>
        )}
      </div>

      {error ? (
        <div className="alert alert-warning py-2 mb-3">{error}</div>
      ) : null}

      <div className="mb-2 small text-muted">100 coins = $1.00</div>

      <div className="mb-2 small text-muted">
        {isAr ? "الحد الأقصى الآن" : "Max usable now"}:{" "}
        <strong>{maxUsableCoins}</strong>
      </div>

      <div className="input-group mb-3">
        <input
          type="number"
          min="0"
          max={maxUsableCoins}
          step="1"
          className="form-control"
          placeholder={isAr ? "أدخل عدد النقاط" : "Enter coins"}
          value={coinsInput}
          onChange={handleInputChange}
          disabled={disabled || loading}
        />

        <button
          type="button"
          className="btn btn-outline-dark"
          onClick={() => setCoinsInput(maxUsableCoins)}
          disabled={disabled || loading || maxUsableCoins <= 0}
        >
          {isAr ? "استخدام الكل" : "Use Max"}
        </button>
      </div>

      <div className="border rounded-3 p-3 bg-light">
        <div className="d-flex justify-content-between mb-2">
          <span>{isAr ? "الإجمالي قبل الخصم" : "Subtotal"}</span>
          <strong>${Number(subtotal || 0).toFixed(2)}</strong>
        </div>

        <div className="d-flex justify-content-between mb-2">
          <span>{isAr ? "النقاط المستخدمة" : "Coins used"}</span>
          <strong>{appliedCoins}</strong>
        </div>

        <div className="d-flex justify-content-between mb-2 text-success">
          <span>{isAr ? "قيمة الخصم" : "Coins discount"}</span>
          <strong>-${discountUsd.toFixed(2)}</strong>
        </div>

        <hr className="my-2" />

        <div className="d-flex justify-content-between">
          <span>{isAr ? "الإجمالي النهائي" : "Final total"}</span>
          <strong>${finalTotal.toFixed(2)}</strong>
        </div>
      </div>
    </div>
  );
}